import type { ErrorEvent, EventHint } from '@sentry/vue'
import * as Sentry from '@sentry/vue'

import { isRequestAbort } from '~/requestAbort'
import { networkErrorGrouping } from '~/noResponseRetry'
import type { UserModule } from '~/types'
import {
  getOpenReplaySessionURL,
  openReplaySessionURLKeyName,
} from '~/observability/openReplay'

export function prepareSentryEvent(
  event: ErrorEvent,
  hint?: EventHint
): ErrorEvent | null {
  // A call we stopped ourselves. A real drop still reaches Sentry.
  if (isRequestAbort(hint?.originalException)) {
    return null
  }
  const grouping = networkErrorGrouping(hint?.originalException)
  if (grouping) {
    event.fingerprint = grouping.fingerprint
    event.tags = { ...(event.tags || {}), ...grouping.tags }
  }
  if (event.exception?.values) {
    for (const exception of event.exception.values) {
      if (
        exception.type === 'CanceledError' &&
        exception.value === 'canceled'
      ) {
        // Fallback for a cancel that arrived with no originalException.
        // isRequestAbort above already drops one we can see on the error object.
        return null
      }
      if (exception.value === 'ResizeObserver loop limit exceeded') {
        // Not an issue per:
        // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
        return null
      }
    }
  }
  // Setting this here so that it will have an appropriate "jumpto" parameter
  const openReplaySessionURL = getOpenReplaySessionURL()
  if (openReplaySessionURL) {
    event.tags = event.tags || {}
    event.tags[openReplaySessionURLKeyName] = openReplaySessionURL
  }
  return event
}

export const install: UserModule = ({ router, app }) => {
  if (process.env.NODE_ENV === 'development') {
    return
  }
  // https://docs.sentry.io/platforms/javascript/guides/vue/
  Sentry.init({
    app,
    dsn: 'https://671d76b9dcd8443794e771edbc8c2881@o946257.ingest.sentry.io/6274045',
    replaysSessionSampleRate: 0.0,
    tracesSampleRate: 0.2,
    attachStacktrace: true,
    denyUrls: [
      /hubspot\.com/i,
      /sentry\.io/i,
      /openreplay\.com/i,
      /crisp\.chat/i,
    ],
    tracePropagationTargets: [/.*api\.draydog\.com.*/],
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
      Sentry.extraErrorDataIntegration(),
      Sentry.reportingObserverIntegration(),
      Sentry.vueIntegration({
        tracingOptions: {
          // See:
          // https://docs.sentry.io/platforms/javascript/guides/vue/features/component-tracking/#trackcomponents
          // I'm not enabling this because I think it might destroy performance, but it might
          // be interesting to try. Maybe activate only on internal users to start
          trackComponents: false,
        },
      }),
    ],
    beforeSend: prepareSentryEvent,
  })
}
